import type { PaymentSession } from "@vaulted/shared";
import { type StripeEmbeddedCheckout, loadStripe } from "@stripe/stripe-js";
import { useEffect, useRef, useState } from "react";
import { errorMessage, post } from "../api";
import { Alert, Button, Loading } from "./ui";

/**
 * Stripe's checkout, embedded in our page (cards, Apple Pay, Google Pay, Link). Without Stripe
 * keys (development, staging before keys are in) it shows a "Complete test payment" button instead.
 */
export function StripeCheckout({ session, onComplete, testLabel = "Complete test payment" }: { session: PaymentSession; onComplete: () => void; testLabel?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const done = useRef(onComplete);
  done.current = onComplete;
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!session.publishableKey) return;
    let checkout: StripeEmbeddedCheckout | null = null;
    let gone = false;
    void (async () => {
      try {
        const stripe = await loadStripe(session.publishableKey!);
        if (!stripe || gone) return;
        checkout = await stripe.createEmbeddedCheckoutPage({ clientSecret: session.clientSecret, onComplete: () => done.current() });
        if (gone) return checkout.destroy();
        checkout.mount(box.current!);
        setReady(true);
      } catch (e) {
        setError(errorMessage(e));
      }
    })();
    return () => {
      gone = true;
      checkout?.destroy();
    };
  }, [session.clientSecret, session.publishableKey]);

  if (!session.publishableKey) {
    return (
      <div className="flex flex-col gap-3 rounded-[var(--radius-panel)] border border-dashed border-line bg-white p-4">
        <p className="text-sm text-ink-soft">Payments are in test mode here (Stripe isn't connected yet). Nothing is charged.</p>
        {error && <Alert>{error}</Alert>}
        <Button
          variant="primary"
          busy={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await post(`/api/dev/payments/${session.sessionRef}/complete`);
              done.current();
            } catch (e) {
              setError(errorMessage(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          {testLabel}
        </Button>
      </div>
    );
  }
  return (
    <div>
      {error && <Alert>{error}</Alert>}
      {!ready && !error && <Loading />}
      <div ref={box} />
    </div>
  );
}
